/* eslint max-lines: 0 */
import React, { useState } from 'react';
import Header from 'components/Modal/Header';
import Table from 'components/Table';
import Dropdown from 'components/Input/dropdown/Dropdown';
import TextInput from 'components/Input/TextInput';
import CommonButton from 'components/Buttons/CommonButton';
import { Formik } from 'formik';
import IntlHelper from 'utils/helpers/intl.helper';
import styled from 'styled-components/macro';
import services from 'services';
import actions from 'state/actions';
import selectors from 'state/selectors';
import { SearchSubject } from 'models/response';
import { useDispatch, useSelector } from 'react-redux';
import { useField } from 'formik';
import { FormData } from '../../pdf/bottom';

const SearchContainer = styled.div`
  display: flex;
  padding-bottom: 25px;
  > div {
    margin-right: 15px;
  }
`;

const TableContainer = styled.div`
  width: 1200px;
  .table-pagination {
    justify-content: center;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  button {
    margin: 0 5px;
  }
`;

const SearchSubjectModal = () => {
  const modalProps = useSelector(selectors.modal.getModalProps);
  const t = IntlHelper.useTranslator();
  const dispatch = useDispatch();
  const [subjectResult, setSubjectResult] = useState<SearchSubject[]>([]);
  const [selectedRow, setSelectedRow] = useState<SearchSubject | null>(null);
  const [fieldCompany] = useField<FormData['company']>('company');
  const [fieldDocType] = useField<FormData['docType']>('docType');
  const [, , helpersFieldSubject] = useField<FormData['subject']>('subject');
  const [, , helpersIdParent] = useField<FormData['idParent']>('idParent');

  const searchTypes = [
    {
      label: 'EQUAL',
      value: 0,
    },
    {
      label: 'LIKE',
      value: 1,
    },
    {
      label: 'DISTANCE',
      value: 2,
    },
  ];

  const keys = [
    { label: t('soggetto'), value: 'SOGGETTO' },
    { label: t('codforn'), value: 'CODFORN' },
    { label: t('piva'), value: 'PIVA' },
    { label: t('codfisc'), value: 'CODFISC' },
    { label: t('tel'), value: 'TEL' },
    { label: t('email'), value: 'EMAIL' },
    { label: t('address'), value: 'ADDRESS' },
  ];

  const columns = [
    {
      Header: t('subject'),
      accessor: 'subject',
    },
    {
      Header: t('codForn'),
      accessor: 'supplierCodesString',
    },
    {
      Header: t('vatNumber'),
      accessor: 'vatNumber',
    },
    {
      Header: t('taxCode'),
      accessor: 'fiscalCode',
    },
    {
      Header: t('tel'),
      accessor: 'tel',
    },
    {
      Header: t('fax'),
      accessor: 'fax',
    },
    {
      Header: t('email'),
      accessor: 'email',
    },
    {
      Header: t('address'),
      accessor: 'address',
    },
  ];

  const onSubmit = async (values: {
    key: { label: string; value: string };
    searchType: { label: string; value: number };
    value: string;
    distance: number;
  }) => {
    if (fieldCompany.value && fieldDocType.value) {
      try {
        const { data } = await services.getSubjects(
          {
            key: values.key.value,
            value: values.value.toUpperCase(),
            searchType: values.searchType.value,
            distance: values.distance,
            idCompany: fieldCompany.value.value,
            idDocType: fieldDocType.value.value,
            fromClient: false,
          },
          'modal',
        );
        setSubjectResult(data);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onSelection = (rows: SearchSubject[]) => {
    setSelectedRow(rows[0]);
  };

  const setSubject = async (id: number, subject: string) => {
    const body = { label: subject, value: id };
    helpersFieldSubject.setValue(body);
    helpersIdParent.setValue(null);
    modalProps.func({ ...modalProps.values, subject: body });
  };

  const saveRecord = () => {
    if (selectedRow) {
      setSubject(selectedRow.idWSubject, selectedRow.subject);
      dispatch(actions.modal.closeModal());
    }
  };

  return (
    <>
      <Header title={t('searchSubjectTitle')} subtitle={t('searchSubjectSubTitle')} />
      <Formik
        initialValues={{
          key: keys[0],
          searchType: searchTypes[1],
          value: '',
          distance: 0,
        }}
        onSubmit={onSubmit}
      >
        {({ values, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <SearchContainer>
              <Dropdown
                id="key-dropdown"
                name="key"
                value={values['key']}
                onChange={(value) => {
                  setFieldValue('key', value);
                }}
                options={keys}
              />
              <Dropdown
                id="search-type-dropdown"
                name="searchType"
                placeholder={t('searchType')}
                value={values['searchType']}
                onChange={(value) => {
                  setFieldValue('searchType', value);
                }}
                options={searchTypes}
              />
              <TextInput
                id="distance"
                value={values['distance']}
                placeholder={t('distance')}
                type="number"
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={values.searchType.value !== 2}
              />
              <TextInput
                id="value"
                value={values['value']}
                placeholder={t('valoreRicerca')}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <CommonButton
                id="search-subject-modal-search"
                scope="primary"
                value={t('search')}
                type="submit"
                disabled={!values['value']}
              />
            </SearchContainer>
          </form>
        )}
      </Formik>
      <TableContainer>
        {subjectResult ? (
          <Table
            id="search-subject-table"
            rowId="idWSubject"
            hasSort
            columns={columns}
            rows={subjectResult}
            onSelection={onSelection}
            onRowDoubleClick={saveRecord}
            hasSelection
            hasPagination
            hasResize
          />
        ) : null}
      </TableContainer>
      <ButtonsContainer>
        <CommonButton
          id="search-subject-modal-confirm"
          scope="primary"
          value={t('confirm')}
          type="button"
          disabled={!selectedRow}
          action={() => saveRecord()}
        />
        <CommonButton
          id="search-subject-modal-cancel"
          scope="secondary"
          value={t('cancel')}
          type="button"
          action={() => dispatch(actions.modal.closeModal())}
        />
      </ButtonsContainer>
    </>
  );
};

export default SearchSubjectModal;
